import { NavLink, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/pgt";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  LayoutDashboard, Users, CalendarClock, Package, FileText,
  BarChart3, UserCog, Settings, ScrollText, LogOut, Menu, X, UserCircle, Loader2,
  Target, Send, Hourglass,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/followups", label: "Follow-ups", icon: CalendarClock },
  { to: "/products", label: "Products", icon: Package },
  { to: "/templates", label: "Templates", icon: FileText },
  { to: "/reports", label: "Reports", icon: BarChart3, roles: ["admin", "manager"] },
  { to: "/users", label: "Users", icon: UserCog, roles: ["admin", "manager"] },
  { to: "/settings", label: "Company & Settings", icon: Settings, roles: ["admin"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, roles: ["admin", "manager"] },
];

const MOBILE_PRIMARY = ["/dashboard", "/customers", "/followups", "/products"];

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export default function Layout({ children }) {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const items = NAV.filter((n) => !n.roles || n.roles.includes(user.role));
  const mobileItems = items.filter((n) => MOBILE_PRIMARY.includes(n.to));

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SideLink = ({ to, label, icon: Icon, onClick }) => (
    <NavLink
      to={to}
      onClick={onClick}
      data-testid={`nav-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          isActive
            ? "bg-forest text-ivory shadow-sm"
            : "text-stone-600 hover:bg-forest-light hover:text-forest"
        }`
      }
    >
      <Icon className="h-[18px] w-[18px] shrink-0" />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen bg-ivory pgt-grain">
      {/* Top header */}
      <header className="sticky top-0 z-40 h-16 border-b border-stone-200 bg-ivory/90 backdrop-blur-md">
        <div className="mx-auto flex h-full max-w-[1400px] items-center gap-3 px-4 md:px-6">
          <button
            className="md:hidden text-stone-700"
            onClick={() => setDrawerOpen(true)}
            data-testid="open-menu-button"
            aria-label="Open menu"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex items-center gap-2.5">
            <div className="grid h-9 w-9 place-items-center overflow-hidden rounded-lg bg-black">
              <img src={`${process.env.PUBLIC_URL}/pgt-logo.png`} alt="Prime Gold Tea" className="h-full w-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-heading text-[15px] font-bold text-forest">Prime Gold Tea</div>
              <div className="text-[10px] uppercase tracking-widest text-gold font-semibold">Sales Copilot</div>
            </div>
          </div>
          <div className="ml-auto">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full border border-stone-200 bg-white px-2 py-1.5 pr-3 hover:border-forest/40 transition-colors"
                  data-testid="user-menu-button"
                >
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-forest text-ivory text-xs font-bold">
                    {initials(user.name)}
                  </span>
                  <span className="hidden sm:block text-left leading-tight">
                    <span className="block text-xs font-semibold text-stone-800">{user.name}</span>
                    <span className="block text-[10px] text-stone-500">{ROLE_LABEL[user.role]}</span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel>
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-xs font-normal text-stone-500">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setProfileOpen(true)} data-testid="profile-menu-item">
                  <UserCircle className="mr-2 h-4 w-4" /> My details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={doLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px]">
        {/* Desktop sidebar */}
        <aside className="hidden md:flex w-60 shrink-0 flex-col gap-1 border-r border-stone-200 p-3 sticky top-16 h-[calc(100vh-4rem)]">
          {items.map((n) => (
            <SideLink key={n.to} {...n} />
          ))}
        </aside>

        {/* Mobile drawer */}
        {drawerOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="absolute inset-0 bg-black/40" onClick={() => setDrawerOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-72 bg-ivory p-4 shadow-xl animate-fade-up">
              <div className="mb-4 flex items-center justify-between">
                <span className="font-heading font-bold text-forest">Menu</span>
                <button onClick={() => setDrawerOpen(false)} data-testid="close-menu-button">
                  <X className="h-5 w-5 text-stone-600" />
                </button>
              </div>
              <div className="flex flex-col gap-1">
                {items.map((n) => (
                  <SideLink key={n.to} {...n} onClick={() => setDrawerOpen(false)} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main content */}
        <main className="min-w-0 flex-1 px-4 py-5 pb-24 md:px-8 md:py-8 md:pb-8">{children}</main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex h-16 items-center justify-around border-t border-stone-200 bg-white/95 backdrop-blur-md md:hidden">
        {mobileItems.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            data-testid={`bottomnav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 text-[10px] font-medium ${
                isActive ? "text-forest" : "text-stone-500"
              }`
            }
          >
            <n.icon className="h-5 w-5" />
            {n.label}
          </NavLink>
        ))}
        <button
          onClick={() => setDrawerOpen(true)}
          className="flex flex-col items-center gap-0.5 text-[10px] font-medium text-stone-500"
          data-testid="bottomnav-more"
        >
          <Menu className="h-5 w-5" />
          More
        </button>
      </nav>

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} user={user} onSaved={setUser} />
      <DailyTaskReminder />
      <IdleCustomerReminder />
    </div>
  );
}

function IdleCustomerReminder() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const isExec = user?.role === "executive";

  const { data, refetch } = useQuery({
    queryKey: ["idle-reminder"],
    queryFn: async () => (await api.get("/reports/idle-trend")).data,
    enabled: isExec,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data?.currently_idle > 0) setOpen(true);
  }, [data?.currently_idle]);

  useEffect(() => {
    if (!isExec) return;
    const id = setInterval(async () => {
      const r = await refetch();
      if (r.data?.currently_idle > 0) setOpen(true);
    }, 2 * 60 * 60 * 1000); // every 2 hours while the app is open
    return () => clearInterval(id);
  }, [isExec, refetch]);

  if (!isExec || !data?.currently_idle) return null;
  const n = data.currently_idle;
  const goList = () => { setOpen(false); navigate("/customers"); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="idle-customer-reminder">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Hourglass className="h-5 w-5 text-amber-500" /> Idle customers need attention
          </DialogTitle>
          <DialogDescription>
            {n === 1 ? "1 of your customers has" : `${n} of your customers have`} had no contact in {data.threshold_days}+ days. A quick message keeps them warm.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-stone-600"><Hourglass className="h-4 w-4 text-amber-500" /> Currently idle</span>
            <span className="font-heading text-2xl font-bold text-amber-600" data-testid="idle-reminder-count">{n}</span>
          </div>
          {data.this_week > 0 && <p className="mt-2 text-xs text-stone-500">{data.this_week} went idle this week.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} data-testid="idle-reminder-dismiss">Later</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={goList} data-testid="idle-reminder-go">Work idle list</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DailyTaskReminder() {
  const [open, setOpen] = useState(false);
  const { data, refetch } = useQuery({
    queryKey: ["daily-task-reminder"],
    queryFn: async () => (await api.get("/daily-tasks/me")).data,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data?.has_task) setOpen(true);
  }, [data?.has_task]);

  useEffect(() => {
    const id = setInterval(async () => {
      const r = await refetch();
      if (r.data?.has_task) setOpen(true);
    }, 60 * 60 * 1000); // every hour
    return () => clearInterval(id);
  }, [refetch]);

  if (!data?.has_task) return null;
  const pct = data.message_target > 0 ? Math.min(100, Math.round((data.messages_sent / data.message_target) * 100)) : 0;
  const done = data.remaining <= 0;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="daily-task-reminder">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Target className="h-5 w-5 text-forest" /> Today's task
          </DialogTitle>
          <DialogDescription>
            {done
              ? "Great work — you've hit today's message target!"
              : `Reminder: send ${data.message_target} messages today. ${data.remaining} to go.`}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-forest/20 bg-forest-light/30 p-4">
          <div className="mb-1.5 flex items-center justify-between text-sm">
            <span className="flex items-center gap-1.5 text-stone-600"><Send className="h-4 w-4" /> Messages sent</span>
            <span className="font-heading text-lg font-bold text-forest" data-testid="reminder-progress">{data.messages_sent} / {data.message_target}</span>
          </div>
          <div className="h-2.5 overflow-hidden rounded-full bg-white">
            <div className={`h-full rounded-full transition-all ${done ? "bg-emerald-500" : "bg-forest"}`} style={{ width: `${pct}%` }} />
          </div>
          {data.note && <p className="mt-3 text-sm text-stone-600">{data.note}</p>}
        </div>
        <DialogFooter>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={() => setOpen(false)} data-testid="reminder-dismiss">Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProfileDialog({ open, onOpenChange, user, onSaved }) {
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) { setName(user.name || ""); setPhone(user.phone || ""); }
  }, [open, user]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await api.patch("/auth/me", { name: name.trim(), phone: phone.trim() });
      onSaved(r.data);
      toast.success("Saved — your name & number will sign off the messages you send");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">My details</DialogTitle>
          <DialogDescription>Your name and contact number appear as the signature on the WhatsApp messages you send to customers.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-stone-500">Your name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="profile-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-stone-500">Your contact number</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. +91 98765 43210" data-testid="profile-phone-input" />
          </div>
        </div>
        <DialogFooter>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={saving} data-testid="profile-save">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null} Save details
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
